import { isDefined, isString } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { dayAndTimeToDate } from '@/App/tiles/orders/utils.ts';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { mapOrderTypeToWebApiPathComponent } from '@/store/orders/algoOrders/helper.ts';
import type { ClippingMode, ExpiryDate, Speed, StartDate } from '@/store/orders/algoOrders/state.ts';
import type { AlgoOrderType, SubmitOrderResponse, ValidationResponseDetailContent } from '@/store/orders/state.ts';
import type { ClientWay } from '@/store/orders/types.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import { getDateNow } from '@/utils/date.ts';

export type UpdateFixingRequest = {
  tileId: string;
  orderType: AlgoOrderType;

  orderRef: string;
  way: ClientWay;
  currency1: string;
  currency2: string;
  notional?: number;
  notionalCurrencyIndex: CurrencyIndex;

  limitPrice?: number;
  startDate: StartDate;
  expiryDate: ExpiryDate; //endDateUtc
  spreadCapture: boolean;
  liquidityPool: string[];
  clippingMode: ClippingMode;
  clipSize?: number;
  randomize: boolean;
  speed?: Speed | null;
  alphaSeeker: boolean;
};
export type UpdateAlgoBody = {
  orderRef: string;
  amountInCcy1?: number;
  amountInCcy2?: number;
  ccyPair: string;
  way: ClientWay;

  limitPrice?: string;
  startDateUtc?: string;
  endDateUtc?: string;
  spreadCapture?: boolean;
  liquidityPool: string;
  clippingMode?: ClippingMode;
  clipSize?: number;
  dodging?: boolean;
  speed?: Speed;
  alphaSeeker: boolean;
};

export const updateAlgo = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<SubmitOrderResponse, UpdateFixingRequest>({
    query: (request) => ({
      url: `/oms/update/${mapOrderTypeToWebApiPathComponent(request.orderType)}`,
      method: 'POST',
      body: generateBody(request),
      headers: {
        // without this header, the response is a string
        // instead of JSON, even if the value is "application/json"
        accept: '*/*',
      },
    }),
    transformResponse: (
      response:
        | string
        | {
            validationResponseDetailContent: ValidationResponseDetailContent[];
          },
    ) => {
      return {
        type: 'success',
        orderId: response as string, // assuming the response is a string with the order ID
      };
    },
    transformErrorResponse: (errorResponse, _meta, arg): SubmitOrderResponse => {
      const tileId = arg.tileId;
      if (errorResponse.status === 'TIMEOUT_ERROR' || errorResponse.status === 504) {
        return {
          type: 'failure',
          errors: [
            {
              field: 'unknown',
              errorDescription: 'timeout',
              errorCode: -1,
            },
          ],
        };
      }

      const data = errorResponse.data as
        | string
        | {
            validationResponseDetailContent: ValidationResponseDetailContent[];
          };
      if (typeof errorResponse.status === 'number' && errorResponse.status >= 400 && errorResponse.status <= 499) {
        logger.logError(
          `Order submission failed because of a validation field rejection. orderId: {tileId_s},
            response: {data_s}`,
          tileId,
          JSON.stringify(data),
        );
        if (isValidateOrderResponse(data)) {
          return {
            type: 'failure',
            errors: data?.validationResponseDetailContent,
          };
        }

        if (isString(data)) {
          return {
            type: 'failure',
            errors: [
              {
                field: 'unknown',
                errorDescription: data,
                errorCode: -1,
              },
            ],
          };
        }
      }

      if (typeof errorResponse.status === 'number' && errorResponse.status >= 500 && errorResponse.status <= 599) {
        logger.logError(
          'Backend crashed with error 500 for orderId: {tileId_s}, response: {data_s}',
          tileId,
          JSON.stringify(data),
        );
        return {
          type: 'failure',
          errors: [
            {
              field: 'unknown',
              errorDescription: 'server.error',
              errorCode: -1,
            },
          ],
        };
      }

      logger.logError(
        'Backend returned an unexpected error {status_s} for orderId: {tileId_s}, response: {data_s}',
        errorResponse.status,
        tileId,
        JSON.stringify(data),
      );

      return {
        type: 'failure',
        errors: [
          {
            field: 'unknown',
            errorDescription: 'order.unexpected',
            errorCode: -1,
          },
        ],
      };
    },
  });
};

const generateBody = (order: UpdateFixingRequest): UpdateAlgoBody => {
  return {
    orderRef: order.orderRef ?? '',
    ccyPair: `${order?.currency1}/${order?.currency2}`,
    way: order.way ?? 'buy',
    amountInCcy1: order.notionalCurrencyIndex === 1 ? order.notional : undefined,
    amountInCcy2: order.notionalCurrencyIndex === 2 ? order.notional : undefined,
    liquidityPool: order.liquidityPool.length > 0 ? order.liquidityPool.join(',') : '',
    startDateUtc: order.startDate.isNow
      ? getDateNow().toISOString()
      : dayAndTimeToDate(order.startDate.date, order.startDate.time),
    endDateUtc: dayAndTimeToDate(order.expiryDate.date, order.expiryDate.time),
    limitPrice: isDefined(order.limitPrice) ? String(order.limitPrice) : undefined,

    clippingMode: isTwap(order) ? order.clippingMode : undefined,
    spreadCapture: isTwap(order) ? order.spreadCapture : undefined,
    clipSize: isTwap(order) && order.clippingMode === 'Manual' ? order.clipSize : undefined,
    dodging: isTwap(order) ? order.randomize : undefined,

    speed: !isTwap(order) ? (order.speed ?? undefined) : undefined,
    alphaSeeker: isNightjar(order) ? order.alphaSeeker : false,
  };
};

const isTwap = (order: UpdateFixingRequest) => order.orderType === 'Twap+Order';

const isNightjar = (order: UpdateFixingRequest) => order.orderType === 'NightjarOrder';

function isValidateOrderResponse(response: unknown): response is {
  validationResponseDetailContent: ValidationResponseDetailContent[];
  isReadyToupdate: boolean;
} {
  return (
    typeof response === 'object' &&
    response !== null &&
    'validationResponseDetailContent' in response &&
    'isReadyToupdate' in response &&
    Array.isArray(response.validationResponseDetailContent)
  );
}
